// hooks/useSiteAvailability.js - OPTIMIZED VERSION
import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from "../../api";

export const useSiteAvailability = (storeIds, dateRange, businessId) => {
  const [siteAvailability, setSiteAvailability] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  
  // Use refs to prevent unnecessary re-renders
  const cacheRef = useRef(new Map());
  const lastParamsRef = useRef({ storeIds: [], dateRange: '', businessId: '' });

  const getDateRangeField = useCallback((range) => {
    const mapping = {
      'today': 'has_data_today',
      'yesterday': 'has_data_yesterday', 
      'week': 'has_data_this_week',
      'month': 'has_data_this_month',
      'year': 'has_data_this_year',
      'all': 'has_data_this_year'
    };
    return mapping[range] || 'has_data_this_week';
  }, []);

  const checkSiteAvailability = useCallback(async () => {
    console.log('📊 checkSiteAvailability called', {
      storeIds: storeIds?.length,
      dateRange,
      businessId,
      hasStoreIds: !!(storeIds?.length)
    });

    if (!storeIds?.length || !dateRange || !businessId) {
      console.log('⚠️ Skipping availability check - missing params:', {
        hasStoreIds: !!(storeIds?.length),
        dateRange,
        businessId
      });
      return;
    }

    // Check if parameters actually changed
    const currentParams = {
      storeIds: storeIds.sort().join(','),
      dateRange,
      businessId
    };

    const lastParams = lastParamsRef.current;
    if (currentParams.storeIds === lastParams.storeIds &&
        currentParams.dateRange === lastParams.dateRange &&
        currentParams.businessId === lastParams.businessId) {
      console.log('⏭️ Skipping - params unchanged');
      return; // No change, skip API call
    }

    // Check cache
    const cacheKey = `${businessId}-${dateRange}`;
    const cached = cacheRef.current.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < 30000) { // 30 second cache
      console.log('✓ Using cached site availability', { cacheKey, age: Date.now() - cached.timestamp });
      setSiteAvailability(cached.data);
      lastParamsRef.current = currentParams;
      return;
    }

    setIsLoading(true);
    console.log(`🌐 Fetching site availability for: ${dateRange}`, { businessId, storeCount: storeIds.length });

    try {
      // Use the existing endpoint WITHOUT date range parameter
      const response = await api.get(`/business/stores-with-availability/${businessId}`);
      const storesData = response.data;
      console.log('✅ Availability data fetched:', {
        totalStores: storesData.length,
        requestedStores: storeIds.length,
        dateRange
      });

      const availability = {};

      storesData.forEach(store => {
        if (storeIds.includes(store.store_id)) {
          // Map date ranges to the appropriate database fields
          let hasDataForRange = false;

          switch (dateRange) {
            case 'today':
              hasDataForRange = Boolean(store.has_data_today);
              break;
            case 'yesterday':
              hasDataForRange = Boolean(store.has_data_yesterday);
              break;
            case 'week':
              hasDataForRange = Boolean(store.has_data_this_week);
              break;
            case 'month':
              hasDataForRange = Boolean(store.has_data_this_month);
              break;
            case 'year':
            case 'all':
              hasDataForRange = Boolean(store.has_data_this_year);
              break;
            default:
              hasDataForRange = Boolean(store.has_data_this_week);
          }

          availability[store.store_id] = hasDataForRange;
          if (hasDataForRange) {
            console.log(`  ✓ Store ${store.store_id}: HAS data for ${dateRange}`);
          }
        }
      });

      console.log('📋 Final availability map:', availability);

      // Cache the result
      cacheRef.current.set(cacheKey, {
        data: availability,
        timestamp: Date.now()
      });

      setSiteAvailability(availability);
      lastParamsRef.current = currentParams;

    } catch (error) {
      console.error('Error checking site availability:', error);
      // Fallback
      const fallback = {};
      storeIds.forEach(id => fallback[id] = true);
      setSiteAvailability(fallback);
    } finally {
      setIsLoading(false);
    }
  }, [storeIds, dateRange, businessId, getDateRangeField]);

  // Only run when dependencies actually change
  useEffect(() => {
    checkSiteAvailability();
  }, [checkSiteAvailability]);

  const refreshAvailability = useCallback(() => {
    console.log('🔄 Refreshing availability - clearing cache');
    cacheRef.current.clear();
    lastParamsRef.current = { storeIds: [], dateRange: '', businessId: '' };
    checkSiteAvailability();
  }, [checkSiteAvailability]);

  return {
    siteAvailability,
    isLoadingAvailability: isLoading,
    refreshAvailability
  };
};